import { isAxiosError } from 'axios'
import { chunk } from 'lodash-es'
import { OrderItem } from '@/services/outbound/outboundWaitingStatus/types'
import { HTTP_BAD_GATEWAY, HTTP_GATEWAY_TIMEOUT, HTTP_SERVICE_UNAVAILABLE } from '@/utils/consts'

/**
 * 미할당 출고지시(운영자 재주입) — LOGISPRD-12714
 *
 * 화면 전용 flag 다. BE 상류의 `outbound.replanning.enable` 과는 별개이고, 상류가 꺼져 있으면
 * 이 flag 가 켜져 있어도 요청은 503 으로 돌아온다.
 */
export const OPERATOR_REPLAN_FLAG = 'feature.outboundUnallocated.replan' as const

/** 인입 경로 — LOGISPRD-12713 이 `standard_order` UK 3키로 여는 운영자 재주입 API. */
export const OPERATOR_REPLAN_URL = '/api/v1/operations/orders/replan'

/** BE `OperatorReplanReason` 은 닫힌 1종이다. 다른 값은 400 으로 거절된다. */
export const OPERATOR_REPLAN_REASON = 'OPTIMIZER_RECLEANSE' as const

/**
 * 출고지시 대상 행. UK 선두가 `shipper_id` 라 화주는 반드시 `shipperCode` 로 보낸다 —
 * `shipper_name` 은 유니크가 아니라 이름을 실으면 유일성 보장이 조용히 증발한다.
 */
export type ReplanTargetRow = Pick<OrderItem, 'shipperCode' | 'orderNumber' | 'orderOptionNumber'>

export interface OperatorReplanItemReq {
  orderNumber: string
  orderOptionNumber: string
}

export interface OperatorReplanReq {
  shipperCode: string
  items: OperatorReplanItemReq[]
  reasonCode: typeof OPERATOR_REPLAN_REASON
  /** 주입 직후 대기를 끊고 즉시 할당하는 레버. 화면은 열지 않는다 — 항상 다음 컷 합류. */
  immediateAllocation: false
}

export const toOperatorReplanReqs = (rows: ReplanTargetRow[]): OperatorReplanReq[] => {
  const rowsByShipper = new Map<string, OperatorReplanItemReq[]>()

  rows.forEach(({ shipperCode, orderNumber, orderOptionNumber }) => {
    const items = rowsByShipper.get(shipperCode) ?? []
    items.push({ orderNumber, orderOptionNumber })
    rowsByShipper.set(shipperCode, items)
  })

  // 화주 최초 등장 순을 지키고, 같은 화주의 묶음은 연속으로 둔다. 요청당 상한을 넘는 분량은 나눠 보낸다.
  return Array.from(rowsByShipper).flatMap(([shipperCode, items]) =>
    chunk(items, OPERATOR_REPLAN_MAX_ROWS).map((chunkedItems) => ({
      shipperCode,
      items: chunkedItems,
      reasonCode: OPERATOR_REPLAN_REASON,
      immediateAllocation: false as const,
    })),
  )
}

export type OperatorReplanItemStatus = 'ACCEPTED' | 'NOT_OPTIMIZER_TARGET' | 'NOT_UNALLOCATED' | 'FAILED'

export interface OperatorReplanItemResult {
  orderNumber: string
  orderOptionNumber: string
  status: OperatorReplanItemStatus
  message?: string | null
}

export interface OperatorReplanBulkResult {
  requested: number
  accepted: number
  rejected: number
  results: OperatorReplanItemResult[]
}

export const OPERATOR_REPLAN_BUTTON_TEXT = '출고지시'

export const OPERATOR_REPLAN_EMPTY_MESSAGE = '선택된 주문이 없습니다.'

/** BE 벌크 계약의 요청당 최대 항목 수. 화주당 이 값을 넘는 선택은 이 크기로 나눠 순차 요청한다. */
export const OPERATOR_REPLAN_MAX_ROWS = 50

export const OPERATOR_REPLAN_CONFIRM_MESSAGE = (count: number) => `총 ${count}건을 출고지시하시겠습니까?`

export const OPERATOR_REPLAN_CONFIRM_SUB_MESSAGE = '미할당 상태를 정리하고 다음 컷에 다시 합류시킵니다.'

/**
 * 갈래가 섞이면 토스트가 여러 장 뜬다. 장마다 분모가 다르면 운영자가 접수분을 역산할 수 없어서,
 * 모든 문구를 이 뼈대 하나로 맞춘다. 전량이 한 갈래면 `2건 중 2건` 이 군더더기라 분모를 뺀다.
 *
 * 반환값은 항상 `건` 으로 끝난다 — 뒤에 붙는 조사(`…건이` / `…건은`)가 이 받침에 기대고 있어서,
 * 끝 글자를 바꾸면 문구들의 조사도 같이 봐야 한다.
 */
const countPhrase = (total: number, count: number) => (total === count ? `${count}건` : `${total}건 중 ${count}건`)

/** 202 응답은 동기 판정 결과다. ACCEPTED 이후의 비동기 처리 결과는 화면이 단정하지 않고 목록에서 확인하게 한다. */
export const OPERATOR_REPLAN_ACCEPTED_MESSAGE = (total: number, count: number) =>
  `${countPhrase(total, count)} 출고지시를 접수했습니다. 처리 결과는 잠시 후 목록에서 확인하세요.`

export const OPERATOR_REPLAN_NOT_OPTIMIZER_TARGET_MESSAGE = (total: number, count: number) =>
  `${countPhrase(total, count)}은 옵티마이저 대상이 아닙니다.`

export const OPERATOR_REPLAN_NOT_UNALLOCATED_MESSAGE = (total: number, count: number) =>
  `${countPhrase(total, count)}은 미할당 상태가 아닙니다.`

const FAILED_REASON_LABEL: Record<string, string> = {
  ORDER_NOT_FOUND: '주문 정보 없음',
  NO_CONSOL: '합포번호 없음',
  NO_REF_NO1: '발주번호 없음',
  VERDICT_ERROR: '옵티마이저 대상 판정 오류',
  NO_STOCK: '판매 가능 재고 없음',
  STOCK_CHECK_FAILED: '재고 확인 실패',
  NO_SKU: 'SKU 정보 없음',
  INBOUND_ERROR: '재주입 판정 오류',
  WORKFLOW_START_ERROR: '워크플로 시작 오류',
  WORKFLOW_START_SKIPPED: '앞선 워크플로 시작 실패로 미처리',
  UNEXPECTED_CANCEL_CONFIRMED: '예상하지 않은 취소 확정',
  UNEXPECTED_CANCEL_DELEGATED: '예상하지 않은 취소 위임',
}

export const OPERATOR_REPLAN_FAILED_MESSAGE = (total: number, messages: Array<string | null | undefined>) => {
  const breakdown = Array.from(
    messages.reduce((counts, message) => {
      const label = (message && FAILED_REASON_LABEL[message]) || '기타 오류'
      counts.set(label, (counts.get(label) ?? 0) + 1)
      return counts
    }, new Map<string, number>()),
    ([label, count]) => `${label} ${count}건`,
  ).join(', ')

  return `${countPhrase(total, messages.length)} 출고지시에 실패했습니다. (${breakdown})`
}

/**
 * 상류 flag 가 꺼진 상태다 — 일시 오류가 아니라 재시도가 무의미하다.
 * 전량이 이 갈래면 건수보다 "기능이 꺼져 있다" 가 핵심이라 건수를 빼고 말한다.
 */
export const OPERATOR_REPLAN_DISABLED_MESSAGE = (total: number, count: number) =>
  total === count
    ? '출고지시 기능이 비활성화되어 있습니다.'
    : `${countPhrase(total, count)}은 출고지시 기능이 비활성화되어 처리되지 않았습니다.`

/** 서버가 meta 를 실어 답한 flag OFF 아닌 503 — 원장 키 조회·워크플로 기동이 넘어진 일시 장애다. 재시도가 유효하다. */
export const OPERATOR_REPLAN_UNAVAILABLE_MESSAGE = (total: number, count: number) =>
  `${countPhrase(total, count)}이 일시적인 오류로 처리되지 않았습니다. 잠시 후 다시 시도해 주세요.`

/** 요청 자체가 실패해(건별 판정 없음) 처리되지 않은 묶음. 건별 FAILED 와 문구를 분리한다. */
export const OPERATOR_REPLAN_REQUEST_FAILED_MESSAGE = (total: number, count: number) =>
  `${countPhrase(total, count)}은 요청이 실패해 처리되지 않았습니다.`

/** 응답을 받지 못한 묶음(응답 없음·502·504·본문 없는 503) — 서버에 접수됐을 수 있어 "실패"라 하지 않고 확인을 안내한다. */
export const OPERATOR_REPLAN_UNCONFIRMED_MESSAGE = (total: number, count: number) =>
  `${countPhrase(total, count)}은 응답을 받지 못해 접수 여부를 확인할 수 없습니다. 잠시 후 목록에서 확인하세요.`

/** 앞선 요청이 실패해 멈추면서 보내지 않은 묶음. */
export const OPERATOR_REPLAN_UNSENT_MESSAGE = (total: number, count: number) =>
  `${countPhrase(total, count)}은 요청을 중단해 보내지 않았습니다. 목록에서 확인 후 다시 출고지시하세요.`

/**
 * `OperatorReplanController` 는 503 을 세 자리에서 던진다 — 상류 flag OFF, 원장 키 조회 실패,
 * 워크플로 기동 실패. 셋 다 `ERROR_SYSTEM` 이라 errorCode 로는 갈리지 않고, flag OFF 만 이 접두사를
 * 달고 온다(`replanning is disabled by outbound.replanning.enable — ...`).
 */
const FEATURE_DISABLED_MESSAGE_PREFIX = 'replanning is disabled by'

export type OperatorReplanFailure =
  | { kind: 'DISABLED' }
  | { kind: 'UNAVAILABLE' }
  | { kind: 'UNCONFIRMED' }
  | { kind: 'UNKNOWN' }

const getFailureMeta = (error: unknown): ApiResponseV2['meta'] | undefined =>
  isAxiosError(error) ? (error.response?.data as ApiResponseV2 | undefined)?.meta : undefined

/**
 * 요청 단위 실패를 화면이 갈라 말할 수 있는 갈래로 환산한다. 항목별 업무 거부는 202 응답의
 * `results` 로 내려오므로 여기서는 503 기능 비활성·일시 장애와 접수 여부 불명(응답 없음·502·504·본문 없는 503)만 구분한다.
 */
export const classifyOperatorReplanFailure = (error: unknown): OperatorReplanFailure => {
  if (!isAxiosError(error)) return { kind: 'UNKNOWN' }

  // 응답이 없거나(시간 초과·네트워크 단절) 게이트웨이 계열(502·504)이면 요청이 서버에 닿았을 수 있어 실패로 단정할 수 없다.
  if (!error.response || error.response.status === HTTP_BAD_GATEWAY || error.response.status === HTTP_GATEWAY_TIMEOUT) {
    return { kind: 'UNCONFIRMED' }
  }

  const { status } = error.response
  const meta = getFailureMeta(error)

  if (status === HTTP_SERVICE_UNAVAILABLE) {
    // 본문(meta) 없는 503 은 서버 컨트롤러가 만든 응답이 아니라 게이트웨이·LB 가 중간에 자른 것이다.
    // 비활성/일시 장애 어느 쪽도 단정할 수 없고 접수 여부도 알 수 없으므로 접수 불명으로 본다.
    if (!meta) return { kind: 'UNCONFIRMED' }
    return meta.message?.startsWith(FEATURE_DISABLED_MESSAGE_PREFIX) ? { kind: 'DISABLED' } : { kind: 'UNAVAILABLE' }
  }

  return { kind: 'UNKNOWN' }
}

export const isFeatureDisabledError = (error: unknown): boolean => classifyOperatorReplanFailure(error).kind === 'DISABLED'
